/**
 * <BulkInterviewCard> — express-mode interview form (hello-weekend #86,
 * Option A).
 *
 * Express mode collapses the trickle-feed interview (one
 * AskUserQuestion turn per question) into a single stacked form. The
 * SKILL pushes the question plan via the `bulk-interview:show` IPC
 * channel; the renderer stashes it in `chatState.pendingBulkInterview`
 * and the chat surface mounts this component in place of the regular
 * bubble timeline. Submit fires `bulk-interview:answer` carrying
 * every answer keyed by question id.
 *
 * Visual treatment: clean, typographic form — no card boxes per row.
 * Questions are separated by hairline dividers with generous vertical
 * rhythm. Single-select options render as rounded pill buttons;
 * free-text rows use a borderless textarea with a bottom rule.
 *
 * Pre-fill semantics (Phase A.2 inferred answers):
 *
 *   - A `prefilled` value populates the row's initial input but is
 *     rendered with reduced opacity until the user touches it (or
 *     accepts it). This signals "we guessed this for you — change it
 *     if we got it wrong."
 *   - Touching a single-select row (clicking any option) treats the
 *     selection as the user's authoritative answer; the prefill
 *     styling lifts.
 *   - Free-text rows lift the prefill styling on first edit.
 *
 * Required-field semantics:
 *
 *   - Submit is disabled until every `required: true` row has a
 *     non-empty answer. Validity is recomputed on every change so
 *     the button enables / disables in real time.
 *   - Optional rows never block Submit, even if they're untouched.
 *
 * Submitted state:
 *
 *   - After Submit, the card collapses to a compact summary that
 *     lists each question + its answer. Mirrors the
 *     `AskUserQuestionCard` post-answer state so the user sees their
 *     submission persisted in the timeline.
 *   - The reducer clears `pendingBulkInterview` on Submit; the
 *     summary state lives entirely INSIDE this component via local
 *     `useState`, so a fresh push that follows immediately mounts a
 *     fresh card. Re-renders driven by `pendingBulkInterview` flips
 *     are what gate the visible component.
 *
 * Cancel:
 *
 *   - Cancel button always available (every form needs an escape
 *     hatch — modeless forms that trap the user are a UX anti-pattern).
 *   - Dispatches `bulk-interview/cancel`; the reducer clears the slice
 *     and emits an `ipc/bulk-interview-answer` effect with
 *     `cancelled: true`. The main-side gate handler resolves the
 *     SKILL's held-open Promise with a "user cancelled" signal so it
 *     falls back to per-question mode without losing partial input.
 *
 * Renderer-only: this component does not call any IPC directly. All
 * side effects flow through `chatDispatch` and the chat reducer's
 * effect interpreter, the same convention every other reducer-driven
 * action uses. Keeps the test surface trivial — pass a fake dispatch
 * and assert the action it produced.
 */

import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import type { BulkInterviewQuestion } from '@volley/crucible-ipc-types/ipc-channels';

// ---------------------------------------------------------------------------
// Pure helpers (extracted so tests can hit them without a DOM)
// ---------------------------------------------------------------------------

/**
 * Build the initial answer map for a question plan: keyed by question id,
 * carrying the prefilled value or an empty string. Untouched-from-empty
 * is distinguishable from prefilled-but-untouched only via the `touched`
 * map (this helper deliberately doesn't try to encode that — see
 * `computeInitialTouched`).
 */
export function computeInitialAnswers(
  questions: readonly BulkInterviewQuestion[],
): Record<string, string> {
  return Object.fromEntries(questions.map((q) => [q.id, q.prefilled ?? '']));
}

/**
 * Build the initial touched map. Every row starts un-touched —
 * a prefilled value reads as "the system guessed this for you" and
 * the visual treatment dims the row until the user edits it. Once
 * touched, the dim lifts.
 */
export function computeInitialTouched(
  questions: readonly BulkInterviewQuestion[],
): Record<string, boolean> {
  return Object.fromEntries(questions.map((q) => [q.id, false]));
}

/**
 * Validity check: every required question has a non-empty answer.
 * Optional questions never block Submit. Pure — extracted so the
 * Submit-disabled matrix can be tested directly without a DOM.
 *
 * A prefilled-but-untouched required row is treated as already
 * filled in (the user accepting the inference by NOT editing it)
 * because `prefilled` populates the answer map at mount time.
 */
export function isBulkInterviewSubmittable(
  questions: readonly BulkInterviewQuestion[],
  answers: Record<string, string>,
): boolean {
  return questions.every((q) => {
    if (!q.required) return true;
    const v = answers[q.id];
    return typeof v === 'string' && v.trim().length > 0;
  });
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/** Imperative handle exposed via forwardRef — lets a parent trigger submit/cancel. */
export interface BulkInterviewCardRef {
  triggerSubmit: () => void;
  triggerCancel: () => void;
}

export interface BulkInterviewCardProps {
  sessionId: string;
  questions: readonly BulkInterviewQuestion[];
  /**
   * Called when the user clicks Submit and every required row has
   * a non-empty answer. Map keys are question ids.
   */
  onSubmit: (answers: Record<string, string>) => void;
  /**
   * Called when the user clicks Cancel. May be invoked at any time,
   * regardless of validation state. Receives whatever partial answers
   * the user typed so the parent can include them in the cancel
   * payload's audit log (the SKILL ignores them when the cancelled
   * flag is set, but main-side logging keeps them around).
   */
  onCancel: (partialAnswers: Record<string, string>) => void;
  /**
   * Fires whenever the submit-armed state changes. Parents that render
   * submit/cancel in a header (via forwardRef) use this to enable/disable
   * their own button without polling.
   */
  onSubmittableChange?: (submittable: boolean) => void;
  /**
   * When true, the bottom submit/cancel row is not rendered. The parent
   * is responsible for providing those actions (e.g. via the ArtifactPane header).
   */
  hideBottomActions?: boolean;
}

/**
 * Map keyed by question id. We carry empty strings for untouched
 * rows so the value lookup stays defined; the validity check filters
 * them when computing whether Submit is allowed.
 */
type AnswerMap = Record<string, string>;

/**
 * Per-question touched flag. Drives the "lift prefill styling" affordance
 * — a row is "touched" once the user picks an option (single-select)
 * or types into the textarea (free-text). Reset to false on the
 * initial render of each question id.
 */
type TouchedMap = Record<string, boolean>;

export const BulkInterviewCard = React.forwardRef<BulkInterviewCardRef, BulkInterviewCardProps>(
  function BulkInterviewCard(
    { sessionId, questions, onSubmit, onCancel, onSubmittableChange, hideBottomActions },
    ref,
  ): React.JSX.Element {
    // Seed answer map from prefilled values. We avoid `useEffect` on
    // `questions` here: the parent only mounts a fresh card when the
    // reducer flips from null → {questions}, so a stable identity is
    // safe to assume for the lifetime of one mount. Re-mounting on a
    // new push is the correct path; trying to merge two plans into one
    // form would risk presenting an ID set the SKILL didn't ask for.
    const [answers, setAnswers] = useState<AnswerMap>(() => computeInitialAnswers(questions));
    const [touched, setTouched] = useState<TouchedMap>(() => computeInitialTouched(questions));

    // Local "submitted" flag — once true, the card switches to its
    // summary state. The chat reducer also clears
    // `pendingBulkInterview` on Submit so the parent stops mounting
    // this component; the summary is therefore brief (one render
    // cycle) on the happy path. Kept anyway so a parent that DOESN'T
    // immediately unmount us (e.g. a test, or a future split where
    // the summary persists in the timeline) shows the post-submit
    // state correctly.
    const [submitted, setSubmitted] = useState<{
      snapshot: AnswerMap;
    } | null>(null);

    /**
     * Validity = every required question has a non-empty answer.
     * Memoised on `answers` + `questions` so a re-render driven by an
     * unrelated parent state change doesn't recompute it. `touched`
     * doesn't gate validity — a prefilled-but-untouched required row
     * is treated as already filled in (the user accepting our inference
     * by not editing it).
     */
    const allRequiredFilled = useMemo(
      () => isBulkInterviewSubmittable(questions, answers),
      [answers, questions],
    );

    // Keep a ref to the latest handlers so useImperativeHandle is stable.
    const handlersRef = useRef({ handleSubmit: () => {}, handleCancel: () => {} });

    useImperativeHandle(ref, () => ({
      triggerSubmit: () => handlersRef.current.handleSubmit(),
      triggerCancel: () => handlersRef.current.handleCancel(),
    }));

    // Notify parent when submittable state changes (fires on mount + changes).
    useEffect(() => {
      onSubmittableChange?.(allRequiredFilled);
    }, [allRequiredFilled, onSubmittableChange]);

    const updateAnswer = (id: string, value: string): void => {
      setAnswers((prev) => ({ ...prev, [id]: value }));
      setTouched((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    };

    const handleSubmit = (): void => {
      if (!allRequiredFilled || submitted) return;
      const snapshot = { ...answers };
      setSubmitted({ snapshot });
      onSubmit(snapshot);
    };

    const handleCancel = (): void => {
      if (submitted) return;
      onCancel({ ...answers });
    };

    // Keep ref up-to-date so the imperative handle always calls the latest closure.
    handlersRef.current = { handleSubmit, handleCancel };

    // Submitted state — compact summary with each question and the answer
    // the user gave.
    if (submitted) {
      return (
        <div
          data-testid="bulk-interview-card-submitted"
          data-session-id={sessionId}
          className="py-2"
        >
          <div className="mb-5 flex items-center gap-2.5">
            <span style={{ color: 'rgb(110,231,183)' }}>✓</span>
            <span className="text-[13px]" style={{ color: 'var(--text-1)', fontWeight: 350 }}>
              Fast questions submitted
            </span>
            <span className="text-[11px]" style={{ color: 'var(--text-3)' }}>
              {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </span>
          </div>
          <div>
            {questions.map((q, idx) => {
              const answer = submitted.snapshot[q.id] ?? '';
              return (
                <React.Fragment key={q.id}>
                  {idx > 0 && (
                    <div className="h-px" style={{ background: 'var(--border-alpha-1)' }} />
                  )}
                  <div className="py-3" data-testid={`bulk-interview-card-summary-row-${q.id}`}>
                    <p
                      className="mb-0.5 text-[12px]"
                      style={{ color: 'var(--text-3)', fontWeight: 350 }}
                    >
                      {q.label}
                    </p>
                    <p className="text-[13px]" style={{ color: 'var(--text-2)', fontWeight: 350 }}>
                      {answer.trim().length > 0 ? answer : '(skipped)'}
                    </p>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div data-testid="bulk-interview-card" data-session-id={sessionId}>
        {/* Form label — required by tests; visually minimal */}
        <p
          className="mb-5 text-[10px] uppercase tracking-[0.08em]"
          style={{ color: 'var(--text-3)', fontWeight: 400 }}
        >
          Fast questions — {questions.length} questions
        </p>

        {/* Question rows */}
        <div>
          {questions.map((q, idx) => (
            <React.Fragment key={q.id}>
              {idx > 0 && <div className="h-px" style={{ background: 'var(--border-alpha-1)' }} />}
              <BulkInterviewRow
                question={q}
                index={idx}
                total={questions.length}
                value={answers[q.id] ?? ''}
                touched={touched[q.id] === true}
                onChange={(v) => updateAnswer(q.id, v)}
              />
            </React.Fragment>
          ))}
        </div>

        {!hideBottomActions && (
          <div
            className="mt-6 flex items-center justify-end gap-2 pt-4"
            style={{ borderTop: '1px solid var(--border-alpha-1)' }}
          >
            <button
              type="button"
              onClick={handleCancel}
              data-testid="bulk-interview-card-cancel"
              className="rounded-full px-4 py-1.5 text-[12px] transition-colors"
              style={{
                color: 'var(--text-3)',
                background: 'var(--surface-pill)',
                boxShadow: 'var(--hairline)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allRequiredFilled}
              data-testid="bulk-interview-card-submit"
              data-armed={allRequiredFilled ? 'true' : 'false'}
              className="rounded-full px-4 py-1.5 text-[12px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                background:
                  'linear-gradient(142deg, rgb(33 69 247 / 0.9) 0%, rgb(33 69 247 / 0.75) 100%)',
                boxShadow: 'inset 0 0 0 1px rgb(33 69 247 / 0.4), 0 2px 8px rgb(33 69 247 / 0.2)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
            >
              Submit answers
            </button>
          </div>
        )}
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// Per-question row
// ---------------------------------------------------------------------------

/**
 * Single row in the bulk-interview form. Switches between
 * option pill group (for `single-select` with non-empty options)
 * and a textarea (for `free-text`, or a defensive fallback when a
 * `single-select` row's options are missing / empty).
 */
function BulkInterviewRow({
  question,
  index,
  total,
  value,
  touched,
  onChange,
}: {
  question: BulkInterviewQuestion;
  index: number;
  total: number;
  value: string;
  touched: boolean;
  onChange: (next: string) => void;
}): React.JSX.Element {
  const isPrefilled = !touched && (question.prefilled ?? '').length > 0;
  const hasOptions =
    question.type === 'single-select' &&
    Array.isArray(question.options) &&
    question.options.length > 0;

  return (
    <div
      className="py-5"
      data-testid={`bulk-interview-row-${question.id}`}
      data-question-type={question.type}
      data-required={question.required ? 'true' : 'false'}
      data-prefilled={isPrefilled ? 'true' : 'false'}
    >
      {/* Meta: position + required + prefilled */}
      <div className="mb-2 flex items-center gap-2.5">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.08em]"
          style={{ color: 'var(--text-3)' }}
        >
          Question {index + 1} of {total}
        </span>
        {question.required && (
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: 'rgba(251,191,36,0.7)' }}
            aria-label="required"
            data-testid={`bulk-interview-row-${question.id}-required-dot`}
          />
        )}
        {isPrefilled && (
          <span
            className="text-[10px] uppercase tracking-[0.06em]"
            style={{ color: 'rgba(252,211,77,0.45)' }}
            data-testid={`bulk-interview-row-${question.id}-prefilled-badge`}
          >
            Pre-filled
          </span>
        )}
      </div>

      {/* Question label */}
      <p
        className="mb-4 text-[14px] leading-snug"
        style={{ color: 'var(--text-1)', fontWeight: 350 }}
      >
        {question.label}
      </p>

      {/* Answer input */}
      {hasOptions ? (
        <div className="flex flex-wrap gap-2">
          {question.options!.map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                data-testid={`bulk-interview-row-${question.id}-option`}
                data-selected={selected ? 'true' : 'false'}
                className="rounded-full px-3.5 py-1.5 text-[13px] transition-all"
                style={{
                  color: selected
                    ? 'var(--text-1)'
                    : isPrefilled
                      ? 'var(--text-3)'
                      : 'var(--text-2)',
                  background: selected ? 'rgb(33 69 247 / 0.18)' : 'var(--surface-pill)',
                  boxShadow: selected ? 'inset 0 0 0 1px rgb(33 69 247 / 0.45)' : 'var(--hairline)',
                  fontWeight: selected ? 500 : 350,
                  transitionTimingFunction: 'var(--ease-flora)',
                  transitionDuration: 'var(--motion-fast)',
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          placeholder="Type your answer…"
          data-testid={`bulk-interview-row-${question.id}-textarea`}
          className="w-full resize-none bg-transparent pb-2 text-[14px] leading-relaxed placeholder:text-white/25 focus:outline-none"
          style={{
            color: isPrefilled ? 'var(--text-3)' : 'var(--text-1)',
            fontStyle: isPrefilled ? 'italic' : 'normal',
            fontWeight: 350,
          }}
        />
      )}
    </div>
  );
}
